import moment from 'moment';
import { formatMoney } from 'helpers/currencyHelper';

/**
 * Open high-definition, printable Tax Invoice PDF in a new tab
 */
export const openInvoicePrintWindow = (bill, clinicData = null) => {
  if (!bill) return;

  const invoiceCurrency = bill.currency_code || clinicData?.currency_code || 'INR';

  // Load custom template configuration if saved
  let tpl = {};
  try {
    const raw = localStorage.getItem("invoice_template_config");
    if (raw) tpl = JSON.parse(raw);
  } catch (e) { }

  const clinicName = clinicData?.clinic_name || "909 Dental OPD & Implantology Center";
  const clinicAddress = clinicData?.address || "Modern Dental Clinic & Oral Health Care Center";
  const clinicPhone = clinicData?.phone || "098220 11111";
  const clinicEmail = clinicData?.email || "billing@oralstop.com";

  const headerTitle = tpl.headerTitle || "INVOICE";
  const tagline = tpl.tagline || "Specialized Dental Care & Oral Health Center";
  const gstNumber = tpl.gstNumber || "";
  const signatoryTitle = tpl.signatoryTitle || "Authorized Signatory";
  const accentColor = tpl.accentColor || "#F8CA15";
  const showBankDetails = tpl.showBankDetails !== false && Boolean(tpl.accountNo);
  const bankName = tpl.bankName || "HDFC Bank";
  const accountNo = tpl.accountNo || "";
  const ifscCode = tpl.ifscCode || "";
  const branch = tpl.branch || "";
  const terms = tpl.terms || "• Thank you for choosing our clinic.\n• This is a computer-generated digital tax invoice.";
  const notes = tpl.notes || "Thank you for trusting 909 Dental OPD with your smile.";

  const patient = bill.patients || bill.patient || {};
  const patientName = `${patient.title || ''} ${patient.first_name || ''} ${patient.last_name || ''}`.trim() ||
    patient.full_name || bill.patient_name || "Patient";
  const caseNo = patient.case_no || bill.patient_id || "N/A";
  const mobile = patient.mobile || bill.patient_mobile || "N/A";

  const invoiceNo = bill.billing_no || `INV-${bill.id}`;
  const dateStr = bill.date ? moment(bill.date).format('DD MMM YYYY') : moment(bill.created_at).format('DD MMM YYYY');

  // Parse itemized procedure rows
  let items = [];
  if (bill.treatment_info) {
    try {
      const parsed = typeof bill.treatment_info === 'string' ? JSON.parse(bill.treatment_info) : bill.treatment_info;
      if (Array.isArray(parsed) && parsed.length > 0) {
        items = parsed;
      } else if (parsed && typeof parsed === 'object') {
        items = [parsed];
      }
    } catch (e) {
      items = [{ _type: String(bill.treatment_info), cost: bill.total, total: bill.total }];
    }
  }

  if (items.length === 0) {
    items = [{
      _type: bill.treatment?.title || "Dental Consultation & Procedure",
      tooth: "",
      cost: bill.total,
      discount: bill.discount || 0,
      total: bill.total
    }];
  }

  const totalBilled = parseFloat(bill.total || 0);
  const paidAmount = parseFloat(bill.paid || 0);
  const pendingDue = parseFloat(bill.pending || 0);

  const statusLabel = pendingDue === 0 && totalBilled > 0 ? "PAID" : (paidAmount > 0 ? "PARTIAL" : "UNPAID");
  const statusColor = statusLabel === "PAID" ? "#16a34a" : (statusLabel === "PARTIAL" ? "#d97706" : "#dc2626");

  const rowsHtml = items.map((itm, idx) => {
    const title = itm._type || itm.type || itm.title || itm.name || "Dental Service";
    const tooth = itm.tooth || itm.teeth ? ` (Tooth #${itm.tooth || itm.teeth})` : '';
    const cost = parseFloat(itm.cost || itm.total || 0);
    const disc = parseFloat(itm.discount || 0);
    const net = parseFloat(itm.total || (cost - disc) || 0);

    return `
      <tr>
        <td style="text-align: center; color: #64748b;">${idx + 1}</td>
        <td>
          <strong style="color: #0f172a;">${title}${tooth}</strong>
          ${itm.notes ? `<div style="font-size: 11px; color: #64748b;">${itm.notes}</div>` : ''}
        </td>
        <td style="text-align: right;">${formatMoney(cost, invoiceCurrency)}</td>
        <td style="text-align: right; color: #64748b;">${formatMoney(disc, invoiceCurrency)}</td>
        <td style="text-align: right; font-weight: 700; color: #0f172a;">${formatMoney(net, invoiceCurrency)}</td>
      </tr>
    `;
  }).join('');

  const logoImage = tpl.logoImage || tpl.logo_image || clinicData?.logo || clinicData?.logoImage || '';
  const signImage = tpl.signImage || tpl.sign_image || clinicData?.signature || clinicData?.signImage || '';

  const html = `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <title>Tax Invoice - ${invoiceNo}</title>
      <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body {
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
          background-color: #f1f5f9;
          color: #334155;
          padding: 30px 15px;
          display: flex;
          justify-content: center;
        }
        .action-bar {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          background: #ffffff;
          border-bottom: 1px solid #e2e8f0;
          padding: 12px 24px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
          z-index: 1000;
        }
        .action-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          border: none;
          text-decoration: none;
        }
        .btn-primary { background: ${accentColor}; color: #ffffff; }
        .btn-primary:hover { opacity: 0.9; }
        .btn-secondary { background: #e2e8f0; color: #334155; }
        .btn-secondary:hover { background: #cbd5e1; }

        .invoice-card {
          background: #ffffff;
          width: 100%;
          max-width: 820px;
          margin-top: 50px;
          padding: 40px;
          border-radius: 12px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
          border: 1px solid #e2e8f0;
        }

        .header-section {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          border-bottom: 2px solid ${accentColor};
          padding-bottom: 20px;
          margin-bottom: 20px;
        }
        .clinic-brand h1 {
          font-size: 22px;
          color: ${accentColor};
          font-weight: 800;
          margin-bottom: 2px;
        }
        .clinic-brand .tagline {
          font-size: 12px;
          color: #475569;
          font-weight: 600;
          margin-bottom: 4px;
        }
        .clinic-brand p {
          font-size: 11px;
          color: #64748b;
          line-height: 1.4;
        }
        .invoice-tag {
          text-align: right;
        }
        .badge-type {
          display: inline-block;
          background: ${accentColor};
          color: #ffffff;
          padding: 5px 14px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 800;
          margin-bottom: 6px;
        }
        .invoice-no {
          font-size: 16px;
          font-weight: 800;
          color: #0f172a;
          font-family: monospace;
        }
        .invoice-date {
          font-size: 12px;
          color: #64748b;
        }

        .meta-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 14px 18px;
          margin-bottom: 24px;
        }
        .meta-col .label {
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: #64748b;
          font-weight: 700;
          margin-bottom: 3px;
        }
        .meta-col .val-title {
          font-size: 14px;
          font-weight: 700;
          color: #0f172a;
        }
        .meta-col .val-sub {
          font-size: 11px;
          color: #475569;
          margin-top: 2px;
        }

        table.items-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 20px;
        }
        table.items-table th {
          background: #f8fafc;
          border-bottom: 2px solid #cbd5e1;
          border-top: 1px solid #e2e8f0;
          padding: 10px 12px;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          color: #475569;
        }
        table.items-table td {
          border-bottom: 1px solid #e2e8f0;
          padding: 11px 12px;
          font-size: 12px;
        }

        .summary-wrapper {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 24px;
          gap: 20px;
        }
        .bank-box {
          flex: 1;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 12px 16px;
          font-size: 11px;
          color: #475569;
        }
        .summary-box {
          width: 300px;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          background: #f8fafc;
          padding: 12px 16px;
        }
        .summary-row {
          display: flex;
          justify-content: space-between;
          font-size: 12px;
          padding: 4px 0;
        }
        .summary-row.total {
          border-top: 2px solid #cbd5e1;
          margin-top: 6px;
          padding-top: 6px;
          font-size: 14px;
          font-weight: 800;
          color: #0f172a;
        }
        .summary-row.paid {
          color: #16a34a;
          font-weight: 700;
        }
        .summary-row.pending {
          color: #dc2626;
          font-weight: 700;
        }

        .footer-note {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          border-top: 1px dashed #cbd5e1;
          padding-top: 20px;
        }
        .footer-note p {
          font-size: 10.5px;
          color: #64748b;
          max-width: 480px;
          line-height: 1.4;
          white-space: pre-line;
        }
        .signatory {
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        .signatory-line {
          width: 160px;
          border-top: 1px solid #475569;
          margin-top: 6px;
          padding-top: 4px;
          font-size: 11px;
          font-weight: 700;
          color: #334155;
        }

        @media print {
          body { background: #ffffff; padding: 0; }
          .action-bar { display: none; }
          .invoice-card {
            margin-top: 0;
            border: none;
            box-shadow: none;
            padding: 15px;
            max-width: 100%;
          }
        }
      </style>
    </head>
    <body>
      <div class="action-bar">
        <div>
          <strong style="color: ${accentColor}; font-size: 14px;">909 Dental OPD • Tax Invoice Viewer</strong>
        </div>
        <div style="display: flex; gap: 10px;">
          <button class="action-btn btn-secondary" onclick="window.close()">✕ Close</button>
          <button class="action-btn btn-primary" onclick="window.print()">🖨️ Print / Save as PDF</button>
        </div>
      </div>

      <div class="invoice-card">
        <div class="header-section">
          <div class="clinic-brand">
            ${logoImage ? `<img src="${logoImage}" style="max-height: 55px; max-width: 180px; object-fit: contain; margin-bottom: 8px; display: block;" alt="Clinic Logo" />` : ''}
            <h1>${clinicName}</h1>
            <div class="tagline">${tagline}</div>
            <p>${clinicAddress}<br>Phone: ${clinicPhone} • Email: ${clinicEmail}</p>
            ${gstNumber ? `<span style="font-size: 10px; color: #475569; font-weight: 600;">GSTIN / Reg No: ${gstNumber}</span>` : ''}
          </div>
          <div class="invoice-tag">
            <div class="badge-type">${headerTitle}</div>
            <div class="invoice-no">#${invoiceNo}</div>
            <div class="invoice-date">Date: ${dateStr}</div>
          </div>
        </div>

        <div class="meta-grid">
          <div class="meta-col">
            <div class="label">Billed Patient</div>
            <div class="val-title">${patientName}</div>
            <div class="val-sub">Case ID: #${caseNo} • Phone: ${mobile}</div>
          </div>
          <div class="meta-col" style="text-align: right;">
            <div class="label">Payment Status & Method</div>
            <div class="val-title" style="color: ${statusColor};">${statusLabel}</div>
            <div class="val-sub">Mode: ${bill.payment_mode || 'Cash / UPI'}</div>
          </div>
        </div>

        <table class="items-table">
          <thead>
            <tr>
              <th style="width: 35px; text-align: center;">#</th>
              <th>Procedure / Clinical Service</th>
              <th style="width: 100px; text-align: right;">Fee</th>
              <th style="width: 80px; text-align: right;">Discount</th>
              <th style="width: 100px; text-align: right;">Net Amount</th>
            </tr>
          </thead>
          <tbody>
            ${rowsHtml}
          </tbody>
        </table>

        <div class="summary-wrapper">
          ${showBankDetails ? `
            <div class="bank-box">
              <strong style="color: #0f172a; display: block; margin-bottom: 3px;">🏦 Bank Transfer Details:</strong>
              Bank: ${bankName}<br>
              A/C No: <strong style="font-family: monospace;">${accountNo}</strong><br>
              IFSC Code: <strong style="font-family: monospace;">${ifscCode}</strong> (${branch})
            </div>
          ` : '<div></div>'}

          <div class="summary-box">
            <div class="summary-row">
              <span>Gross Amount:</span>
              <span>${formatMoney(totalBilled, invoiceCurrency)}</span>
            </div>
            <div class="summary-row">
              <span>Total Discount:</span>
              <span>-${formatMoney(parseFloat(bill.discount || 0), invoiceCurrency)}</span>
            </div>
            <div class="summary-row total">
              <span>Total Payable:</span>
              <span>${formatMoney(totalBilled, invoiceCurrency)}</span>
            </div>
            <div class="summary-row paid">
              <span>Amount Received:</span>
              <span>${formatMoney(paidAmount, invoiceCurrency)}</span>
            </div>
            <div class="summary-row pending">
              <span>Balance Due:</span>
              <span>${formatMoney(pendingDue, invoiceCurrency)}</span>
            </div>
          </div>
        </div>

        <div class="footer-note">
          <p>
            • ${notes}<br>
            ${terms}
          </p>
          <div class="signatory">
            ${signImage ? `<img src="${signImage}" style="max-height: 80px; max-width: 190px; height: 75px; object-fit: contain; margin-bottom: 4px; display: block; margin-left: auto; margin-right: auto;" alt="Signature" />` : ''}
            <div class="signatory-line" style="width: 180px;">${signatoryTitle}</div>
          </div>
        </div>
      </div>

      <script>
        window.addEventListener('load', () => {
          setTimeout(() => { window.print(); }, 500);
        });
      </script>
    </body>
    </html>
  `;

  const newWindow = window.open('', '_blank');
  if (newWindow) {
    newWindow.document.write(html);
    newWindow.document.close();
  }
};

/**
 * Open high-definition, printable Money Receipt PDF in a new tab
 */
export const openReceiptPrintWindow = (payment, clinicData = null) => {
  if (!payment) return;

  const receiptCurrency = payment.currency_code || clinicData?.currency_code || 'INR';

  // Load custom template configuration if saved
  let rTpl = {};
  try {
    const raw = localStorage.getItem("receipt_template_config");
    if (raw) rTpl = JSON.parse(raw);
  } catch (e) { }

  const clinicName = clinicData?.clinic_name || "909 Dental OPD & Implantology Center";
  const clinicAddress = clinicData?.address || "Modern Dental Clinic & Oral Health Care Center";
  const clinicPhone = clinicData?.phone || "098220 11111";
  const clinicEmail = clinicData?.email || "billing@oralstop.com";

  const headerTitle = rTpl.headerTitle || "OFFICIAL MONEY RECEIPT";
  const tagline = rTpl.tagline || "Payment Acknowledgement & Financial Voucher";
  const thanksNote = rTpl.thanksNote || "Received with thanks towards specialized dental treatment and clinical services.";
  const signatoryTitle = rTpl.signatoryTitle || "Accounts Reception Desk";
  const accentColor = rTpl.accentColor || "#059669";
  const borderStyle = rTpl.borderStyle || "dashed";
  const showStamp = rTpl.showStamp !== false;
  const footerNote = rTpl.footerNote || "• Thank you for your payment.\n• Valid electronic payment entry.";

  const patient = payment.patient || payment.patients || {};
  const patientName = `${patient.title || ''} ${patient.first_name || ''} ${patient.last_name || ''}`.trim() ||
    patient.full_name || payment.patient_name || "Patient";
  const caseNo = patient.case_no || payment.patient_id || "N/A";
  const mobile = patient.mobile || payment.patient_mobile || "N/A";

  const receiptNo = payment.receipt_no || `REC-${payment.id}`;
  const paymentDate = payment.payment_date ? moment(payment.payment_date).format('DD MMM YYYY, hh:mm A') :
    (payment.created_at ? moment(payment.created_at).format('DD MMM YYYY, hh:mm A') : moment().format('DD MMM YYYY, hh:mm A'));
  const amountVal = parseFloat(payment.amount || 0);
  const paymentMode = payment.payment_mode || "Cash";
  const invoiceRef = payment.billing_no && payment.billing_no !== 'ADVANCE' ? `Invoice #${payment.billing_no}` : "Advance Treatment Deposit / Consultation";

  const borderCss = borderStyle === 'none' ? '1px solid #e2e8f0' : `2px ${borderStyle} ${accentColor}`;
  const logoImage = rTpl.logoImage || rTpl.logo_image || clinicData?.logo || clinicData?.logoImage || '';
  const signImage = rTpl.signImage || rTpl.sign_image || clinicData?.signature || clinicData?.signImage || '';

  const html = `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <title>Payment Receipt - ${receiptNo}</title>
      <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body {
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
          background-color: #f1f5f9;
          color: #334155;
          padding: 30px 15px;
          display: flex;
          justify-content: center;
        }
        .action-bar {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          background: #ffffff;
          border-bottom: 1px solid #e2e8f0;
          padding: 12px 24px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
          z-index: 1000;
        }
        .action-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          border: none;
          text-decoration: none;
        }
        .btn-success { background: ${accentColor}; color: #ffffff; }
        .btn-success:hover { opacity: 0.9; }
        .btn-secondary { background: #e2e8f0; color: #334155; }

        .receipt-card {
          background: #ffffff;
          width: 100%;
          max-width: 680px;
          margin-top: 50px;
          padding: 36px;
          border-radius: 12px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          border: ${borderCss};
        }

        .receipt-header {
          text-align: center;
          border-bottom: 1px solid #e2e8f0;
          padding-bottom: 16px;
          margin-bottom: 18px;
        }
        .receipt-header h1 {
          font-size: 20px;
          color: ${accentColor};
          font-weight: 800;
          margin-bottom: 2px;
        }
        .receipt-header .tagline {
          font-size: 11.5px;
          color: #475569;
          font-weight: 600;
          margin-bottom: 4px;
        }
        .receipt-header p {
          font-size: 11px;
          color: #64748b;
          line-height: 1.4;
          margin-bottom: 8px;
        }
        .badge-pill {
          display: inline-block;
          background: ${accentColor}15;
          color: ${accentColor};
          border: 1px solid ${accentColor}30;
          font-size: 11px;
          font-weight: 800;
          padding: 4px 14px;
          border-radius: 20px;
          letter-spacing: 0.5px;
        }

        .meta-row {
          display: flex;
          justify-content: space-between;
          font-size: 12px;
          border-bottom: 1px solid #f1f5f9;
          padding-bottom: 10px;
          margin-bottom: 16px;
        }

        .receipt-body {
          margin-bottom: 20px;
        }
        .detail-row {
          margin-bottom: 12px;
        }
        .detail-row .label {
          font-size: 10px;
          text-transform: uppercase;
          color: #64748b;
          font-weight: 700;
          margin-bottom: 2px;
        }
        .detail-row .value {
          font-size: 13.5px;
          font-weight: 700;
          color: #0f172a;
        }

        .amount-box {
          background: ${accentColor}10;
          border: 1px solid ${accentColor}30;
          border-radius: 8px;
          padding: 14px 18px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin: 18px 0;
        }
        .amount-label {
          font-size: 11px;
          font-weight: 700;
          color: ${accentColor};
          text-transform: uppercase;
        }
        .amount-val {
          font-size: 24px;
          font-weight: 800;
          color: ${accentColor};
        }

        .footer-section {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          border-top: 1px solid #e2e8f0;
          padding-top: 16px;
        }
        .footer-note {
          font-size: 10.5px;
          color: #64748b;
          line-height: 1.4;
          white-space: pre-line;
        }
        .signatory {
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        .sign-line {
          width: 140px;
          border-top: 1px solid #475569;
          margin-top: 6px;
          padding-top: 4px;
          font-size: 11px;
          font-weight: 700;
          color: #334155;
        }

        @media print {
          body { background: #ffffff; padding: 0; }
          .action-bar { display: none; }
          .receipt-card {
            margin-top: 0;
            border: ${borderCss};
            box-shadow: none;
            padding: 20px;
            max-width: 100%;
          }
        }
      </style>
    </head>
    <body>
      <div class="action-bar">
        <div>
          <strong style="color: ${accentColor}; font-size: 14px;">909 Dental OPD • Money Receipt Viewer</strong>
        </div>
        <div style="display: flex; gap: 10px;">
          <button class="action-btn btn-secondary" onclick="window.close()">✕ Close</button>
          <button class="action-btn btn-success" onclick="window.print()">🖨️ Print / Save as PDF</button>
        </div>
      </div>

      <div class="receipt-card">
        <div class="receipt-header">
          ${logoImage ? `<img src="${logoImage}" style="max-height: 55px; max-width: 180px; object-fit: contain; margin: 0 auto 8px auto; display: block;" alt="Clinic Logo" />` : ''}
          <h1>${clinicName}</h1>
          <div class="tagline">${tagline}</div>
          <p>${clinicAddress}<br>Phone: ${clinicPhone} • Email: ${clinicEmail}</p>
          <div class="badge-pill">${headerTitle}</div>
        </div>

        <div class="meta-row">
          <div>
            <span style="color: #64748b;">Receipt No:</span>
            <strong style="font-family: monospace; font-size: 13px; color: #0f172a;">#${receiptNo}</strong>
          </div>
          <div>
            <span style="color: #64748b;">Payment Date:</span>
            <strong style="color: #0f172a;">${paymentDate}</strong>
          </div>
        </div>

        <div class="receipt-body">
          <div class="detail-row">
            <div class="label">Received With Thanks From:</div>
            <div class="value">${patientName}</div>
            <div style="font-size: 11px; color: #64748b; margin-top: 2px;">
              Case ID: #${caseNo} • Mobile: ${mobile}
            </div>
          </div>

          <div class="detail-row">
            <div class="label">Payment For / Invoice Reference:</div>
            <div class="value" style="color: ${accentColor};">${invoiceRef}</div>
          </div>

          <div class="detail-row">
            <div class="label">Payment Method:</div>
            <div class="value">${paymentMode}</div>
          </div>

          ${payment.remark ? `
            <div class="detail-row">
              <div class="label">Remarks / Notes:</div>
              <div style="font-size: 11.5px; color: #475569;">${payment.remark}</div>
            </div>
          ` : ''}

          <div class="amount-box">
            <div>
              <div class="amount-label">Total Amount Received</div>
              <div style="font-size: 10.5px; color: ${accentColor}; margin-top: 2px;">Status: Settled & Verified</div>
            </div>
            <div class="amount-val">${formatMoney(amountVal, receiptCurrency)}</div>
          </div>
        </div>

        <div class="footer-section">
          <div class="footer-note">
            ${showStamp ? `<div style="color: #16a34a; font-weight: 700; margin-bottom: 2px;">✓ Verified electronic digital payment receipt</div>` : ''}
            ${thanksNote}<br>
            ${footerNote}
          </div>
          <div class="signatory">
            ${signImage ? `<img src="${signImage}" style="max-height: 80px; max-width: 190px; height: 75px; object-fit: contain; margin-bottom: 4px; display: block; margin-left: auto; margin-right: auto;" alt="Signature" />` : ''}
            <div class="sign-line" style="width: 180px;">${signatoryTitle}</div>
          </div>
        </div>
      </div>

      <script>
        window.addEventListener('load', () => {
          setTimeout(() => { window.print(); }, 500);
        });
      </script>
    </body>
    </html>
  `;

  const newWindow = window.open('', '_blank');
  if (newWindow) {
    newWindow.document.write(html);
    newWindow.document.close();
  }
};

/**
 * Open high-definition, printable Medical/Dental Prescription PDF in a new tab
 */
export const openPrescriptionPrintWindow = (rxData, clinicData = null, doctorData = null, customConfig = null) => {
  if (!rxData) return;

  // Load custom template configuration if saved
  let pTpl = customConfig || {};
  if (!customConfig) {
    try {
      const raw = localStorage.getItem("prescription_template_config");
      if (raw) pTpl = JSON.parse(raw);
    } catch (e) { }
  }

  const clinicName = clinicData?.clinic_name || "909 Dental OPD & Implantology Center";
  const clinicAddress = clinicData?.address || "Modern Dental Clinic & Oral Health Care Center";
  const clinicPhone = clinicData?.phone || "098220 11111";
  const clinicEmail = clinicData?.email || "doctor@oralstop.com";

  const doctorName = doctorData?.name || rxData?.doctor_name || rxData?.doctor?.name || "Dr. Praveen Kumar, MDS";
  const doctorDegree = doctorData?.qualification || rxData?.doctor_qualification || "Oral & Maxillofacial Surgeon | Implantologist";
  const doctorRegNo = doctorData?.registration_no || rxData?.doctor_reg_no || "DCI-REG-88291";

  const headerTitle = pTpl.headerTitle || "CLINICAL PRESCRIPTION";
  const tagline = pTpl.tagline || "Advanced Dental Surgery, Endodontics & Smile Architecture";
  const accentColor = pTpl.accentColor || "#0284c7";
  const theme = pTpl.theme || "modern_clinical"; // modern_clinical, classic_letterhead, dental_dual_column, compact_a5
  const showVitals = pTpl.showVitals !== false;
  const showDiagnosis = pTpl.showDiagnosis !== false;
  const showDoctorSign = pTpl.showDoctorSign !== false;
  const generalAdvice = pTpl.generalAdvice || "• Take all prescribed medicines at specified intervals.\n• Avoid chewing hard food on treated side.\n• Contact clinic immediately in case of acute swelling or drug allergy.";
  const footerNote = pTpl.footerNote || "Valid for 15 days from date of issue. Keep out of reach of children.";

  const patient = rxData.patient || rxData.patients || {};
  const patientName = `${patient.title || ''} ${patient.first_name || ''} ${patient.last_name || ''}`.trim() ||
    patient.full_name || rxData.patient_name || "Patient";
  const caseNo = patient.case_no || rxData.patient_id || "909-P-101";
  const ageGender = `${patient.age ? `${patient.age} Yrs` : ''} ${patient.gender || ''}`.trim() || "Adult";
  const mobile = patient.mobile || rxData.patient_mobile || "N/A";
  const dateStr = rxData.date ? moment(rxData.date).format('DD MMM YYYY') : moment().format('DD MMM YYYY');

  // Parse medicines
  let medicines = [];
  if (rxData.medicins || rxData.medicines) {
    try {
      const rawMeds = rxData.medicins || rxData.medicines;
      medicines = typeof rawMeds === 'string' ? JSON.parse(rawMeds) : rawMeds;
    } catch {
      medicines = [];
    }
  }

  const diagnosisText = rxData.diagnosis || rxData.complaints || "Acute Irreversible Pulpitis / Post-Op Care";

  const medsRowsHtml = Array.isArray(medicines) && medicines.length > 0
    ? medicines.map((m, idx) => `
        <tr>
          <td style="text-align: center; color: #64748b; font-weight: 700; width: 30px;">${idx + 1}</td>
          <td>
            <div style="font-weight: 800; font-size: 13.5px; color: #0f172a;">${m.medicine || m.name || 'Medicine'}</div>
            ${m.molucule ? `<div style="font-size: 11px; color: #64748b;">${m.molucule}</div>` : ''}
            ${m.note ? `<div style="font-size: 11px; color: #b45309; font-weight: 600;">⚠ ${m.note}</div>` : ''}
          </td>
          <td style="text-align: center; font-size: 12px; font-weight: 700; color: #334155;">${m.dose || '1 Tab'}</td>
          <td style="text-align: center;">
            <span style="background: ${accentColor}15; color: ${accentColor}; border: 1px solid ${accentColor}35; font-size: 11.5px; font-weight: 800; padding: 3px 10px; border-radius: 12px;">
              ${m.frequent || '1-0-1'}
            </span>
          </td>
          <td style="text-align: center; font-size: 12px; color: #475569; font-weight: 600;">${m.duration || '5 days'}</td>
          <td style="text-align: right; font-size: 11.5px; color: #0284c7; font-weight: 600;">${m.instructions || 'After Food'}</td>
        </tr>
      `).join('')
    : `<tr><td colspan="6" style="text-align: center; color: #94a3b8; padding: 20px;">No prescribed medications listed.</td></tr>`;

  const logoImage = pTpl.logoImage || pTpl.logo_image || clinicData?.logo || clinicData?.logoImage || '';
  const signImage = pTpl.signImage || pTpl.sign_image || doctorData?.signature || clinicData?.signature || clinicData?.signImage || '';

  const html = `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <title>Prescription - ${patientName} (${dateStr})</title>
      <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body {
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
          background-color: #f8fafc;
          color: #334155;
          padding: 30px 15px;
          display: flex;
          justify-content: center;
        }
        .action-bar {
          position: fixed;
          top: 0; left: 0; right: 0;
          background: #ffffff;
          border-bottom: 1px solid #e2e8f0;
          padding: 12px 24px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
          z-index: 1000;
        }
        .action-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          border: none;
          text-decoration: none;
        }
        .btn-primary { background: ${accentColor}; color: #ffffff; }
        .btn-secondary { background: #e2e8f0; color: #334155; }

        .rx-card {
          background: #ffffff;
          width: 100%;
          max-width: 820px;
          margin-top: 45px;
          padding: 36px 40px;
          border-radius: 12px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.08);
          border: 1px solid #e2e8f0;
          min-height: 980px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }

        .clinic-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          border-bottom: 2px solid ${accentColor};
          padding-bottom: 16px;
          margin-bottom: 16px;
        }
        .clinic-title h1 {
          font-size: 22px;
          font-weight: 900;
          color: ${accentColor};
          letter-spacing: -0.5px;
        }
        .clinic-title .tagline {
          font-size: 12px;
          color: #475569;
          font-weight: 600;
          margin-top: 2px;
        }
        .clinic-title p {
          font-size: 11px;
          color: #64748b;
          margin-top: 4px;
          line-height: 1.4;
        }
        .doctor-box {
          text-align: right;
        }
        .doctor-box .doc-name {
          font-size: 16px;
          font-weight: 800;
          color: #0f172a;
        }
        .doctor-box .doc-deg {
          font-size: 11.5px;
          color: #475569;
          font-weight: 600;
          margin-top: 2px;
        }
        .doctor-box .doc-reg {
          font-size: 10.5px;
          color: ${accentColor};
          font-weight: 700;
          margin-top: 2px;
        }

        .patient-banner {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 10px 16px;
          display: grid;
          grid-template-columns: 2fr 1fr 1fr 1fr;
          gap: 12px;
          margin-bottom: 18px;
          font-size: 12px;
        }
        .patient-banner .item-label {
          font-size: 10px;
          color: #64748b;
          text-transform: uppercase;
          font-weight: 700;
          margin-bottom: 2px;
        }
        .patient-banner .item-val {
          font-weight: 700;
          color: #0f172a;
        }

        .diagnosis-strip {
          background: #eff6ff;
          border-left: 4px solid ${accentColor};
          padding: 8px 12px;
          border-radius: 0 6px 6px 0;
          margin-bottom: 18px;
          font-size: 12px;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .rx-symbol {
          font-family: Georgia, serif;
          font-size: 26px;
          font-weight: 900;
          color: ${accentColor};
          margin-right: 6px;
        }

        table.rx-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 20px;
        }
        table.rx-table th {
          background: #f8fafc;
          border-bottom: 2px solid #cbd5e1;
          padding: 9px 10px;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          color: #475569;
        }
        table.rx-table td {
          border-bottom: 1px solid #f1f5f9;
          padding: 10px;
          vertical-align: middle;
        }

        .footer-advice {
          border-top: 1px dashed #cbd5e1;
          padding-top: 16px;
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          gap: 20px;
        }
        .advice-box {
          font-size: 11px;
          color: #475569;
          line-height: 1.5;
          max-width: 480px;
          white-space: pre-line;
        }
        .doctor-sign {
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        .doctor-sign .sign-line {
          width: 170px;
          border-top: 1px solid #334155;
          padding-top: 4px;
          font-size: 11px;
          font-weight: 700;
          color: #0f172a;
          margin-top: 6px;
        }

        @media print {
          body { background: #ffffff; padding: 0; }
          .action-bar { display: none; }
          .rx-card {
            margin-top: 0;
            border: none;
            box-shadow: none;
            padding: 15px;
            max-width: 100%;
          }
        }
      </style>
    </head>
    <body>
      <div class="action-bar">
        <div>
          <strong style="color: ${accentColor}; font-size: 14px;">909 Dental OPD • Digital Prescription Pad</strong>
        </div>
        <div style="display: flex; gap: 10px;">
          <button class="action-btn btn-secondary" onclick="window.close()">✕ Close</button>
          <button class="action-btn btn-primary" onclick="window.print()">🖨️ Print Prescription</button>
        </div>
      </div>

      <div class="rx-card">
        <div>
          <!-- Header -->
          <div class="clinic-header">
            <div class="clinic-title">
              ${logoImage ? `<img src="${logoImage}" style="max-height: 55px; max-width: 180px; object-fit: contain; margin-bottom: 8px; display: block;" alt="Clinic Logo" />` : ''}
              <h1>${clinicName}</h1>
              <div class="tagline">${tagline}</div>
              <p>${clinicAddress}<br>Phone: ${clinicPhone} • Email: ${clinicEmail}</p>
            </div>
            <div class="doctor-box">
              <div class="doc-name">${doctorName}</div>
              <div class="doc-deg">${doctorDegree}</div>
              <div class="doc-reg">Reg. No: ${doctorRegNo}</div>
            </div>
          </div>

          <!-- Patient Banner -->
          <div class="patient-banner">
            <div>
              <div class="item-label">Patient Name</div>
              <div class="item-val">${patientName}</div>
            </div>
            <div>
              <div class="item-label">Case ID / PID</div>
              <div class="item-val">#${caseNo}</div>
            </div>
            <div>
              <div class="item-label">Age / Gender</div>
              <div class="item-val">${ageGender}</div>
            </div>
            <div style="text-align: right;">
              <div class="item-label">Date</div>
              <div class="item-val">${dateStr}</div>
            </div>
          </div>

          <!-- Clinical Diagnosis Strip -->
          ${showDiagnosis ? `
            <div class="diagnosis-strip">
              <strong style="color: #1e40af;">Clinical Diagnosis & Notes:</strong>
              <span>${diagnosisText}</span>
            </div>
          ` : ''}

          <!-- Rx Symbol and Table -->
          <div style="display: flex; align-items: center; margin-bottom: 8px;">
            <span class="rx-symbol">℞</span>
            <span style="font-size: 12px; font-weight: 800; color: #0f172a; text-transform: uppercase; letter-spacing: 0.5px;">
              Prescribed Medications
            </span>
          </div>

          <table class="rx-table">
            <thead>
              <tr>
                <th style="width: 30px; text-align: center;">#</th>
                <th>Medicine & Formulation</th>
                <th style="width: 90px; text-align: center;">Dosage</th>
                <th style="width: 100px; text-align: center;">Frequency</th>
                <th style="width: 90px; text-align: center;">Duration</th>
                <th style="width: 110px; text-align: right;">Instructions</th>
              </tr>
            </thead>
            <tbody>
              ${medsRowsHtml}
            </tbody>
          </table>
        </div>

        <!-- Footer -->
        <div class="footer-advice">
          <div class="advice-box">
            <strong style="color: #0f172a; display: block; margin-bottom: 2px;">Patient Advice & Instructions:</strong>
            ${generalAdvice}
            <div style="font-size: 10px; color: #94a3b8; margin-top: 4px;">${footerNote}</div>
          </div>
          <div class="doctor-sign">
            ${signImage ? `<img src="${signImage}" style="max-height: 80px; max-width: 190px; height: 75px; object-fit: contain; margin-bottom: 4px; display: block; margin-left: auto; margin-right: auto;" alt="Signature" />` : ''}
            <div class="sign-line" style="width: 180px;">${doctorName}</div>
            <div style="font-size: 10px; color: #64748b; margin-top: 2px;">Authorized Medical Signature</div>
          </div>
        </div>
      </div>

      <script>
        window.addEventListener('load', () => {
          setTimeout(() => { window.print(); }, 500);
        });
      </script>
    </body>
    </html>
  `;

  const newWindow = window.open('', '_blank');
  if (newWindow) {
    newWindow.document.write(html);
    newWindow.document.close();
  }
};

export const printPrescription = openPrescriptionPrintWindow;

/**
 * Open high-definition, printable Full-Mouth Periodontal Examination Chart in a new tab
 */
export const openPeriodontalPrintWindow = (chartData, patientData = null, clinicData = null, doctorName = "Attending Dentist") => {
  if (!chartData) return;

  const rawUpper = typeof chartData.upper === 'string' ? JSON.parse(chartData.upper) : chartData.upper;
  const rawLower = typeof chartData.lower === 'string' ? JSON.parse(chartData.lower) : chartData.lower;

  const clinicName = clinicData?.clinic_name || "909 Dental OPD & Implantology Center";
  const clinicAddress = clinicData?.address || "Modern Dental Clinic & Oral Health Care Center";
  const clinicPhone = clinicData?.phone || "098220 11111";
  const clinicEmail = clinicData?.email || "clinical@oralstop.com";
  const logoImage = clinicData?.logo || clinicData?.logo_image || clinicData?.logoImage || '';
  const signImage = clinicData?.signature || clinicData?.sign_image || clinicData?.signImage || '';

  const patientName = `${patientData?.title || ''} ${patientData?.first_name || ''} ${patientData?.last_name || ''}`.trim() || patientData?.full_name || "Patient";
  const uhid = patientData?.case_no || patientData?.id ? `#${patientData?.case_no || patientData?.id}` : "N/A";
  const ageGender = [patientData?.age ? `${patientData.age} Yrs` : '', patientData?.gender || ''].filter(Boolean).join(' / ') || "N/A";
  const examDate = chartData.date ? moment(chartData.date).format('DD MMM YYYY') : moment().format('DD MMM YYYY');

  const upperTeeth = [18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28];
  const lowerTeeth = [48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38];

  // Compute Summary Statistics
  let bopCount = 0;
  let plaqueCount = 0;
  let deepPocketsCount = 0;
  let totalSitesExamined = 0;
  let implantCount = 0;
  let mobilityCount = 0;

  const processRowStats = (rowKey, rowDef) => {
    if (!rowDef?.data) return;
    rowDef.data.forEach(item => {
      if (rowKey === 'Bleeding_on_probing') {
        if (item.value1 == 1) bopCount++;
        if (item.value2 == 1) bopCount++;
        if (item.value3 == 1) bopCount++;
        totalSitesExamined += 3;
      }
      if (rowKey === 'Plaque') {
        if (item.value1 == 1) plaqueCount++;
        if (item.value2 == 1) plaqueCount++;
        if (item.value3 == 1) plaqueCount++;
      }
      if (rowKey === 'Probing_Depth') {
        [item.value, item.value2, item.value3].forEach(v => {
          const num = Number(v);
          if (!isNaN(num) && num >= 4) deepPocketsCount++;
        });
      }
      if (rowKey === 'Implant' && item.value == 1) implantCount++;
      if (rowKey === 'Mobility' && Number(item.value) > 0) mobilityCount++;
    });
  };

  ['buccal', 'lingual'].forEach(pos => {
    if (rawUpper?.[pos]) {
      Object.keys(rawUpper[pos]).forEach(k => processRowStats(k, rawUpper[pos][k]));
    }
    if (rawLower?.[pos]) {
      Object.keys(rawLower[pos]).forEach(k => processRowStats(k, rawLower[pos][k]));
    }
  });

  const bopPercentage = totalSitesExamined > 0 ? Math.round((bopCount / totalSitesExamined) * 100) : 0;
  const plaquePercentage = totalSitesExamined > 0 ? Math.round((plaqueCount / totalSitesExamined) * 100) : 0;

  const renderPrintCell = (rowKey, rowDef, item) => {
    if (!item) return '-';

    if (rowDef?.type === 'text') {
      const isTriSite = item.value2 !== undefined;
      if (isTriSite) {
        const renderSub = (val) => {
          const num = Number(val);
          const isDeep = !isNaN(num) && num >= 4 && rowKey === 'Probing_Depth';
          const isZero = val === 0 || val === '0' || val === '' || val === undefined;
          const displayVal = isZero ? (rowKey === 'Probing_Depth' ? '0' : '0') : val;
          return `<span style="display:inline-block; width:13px; text-align:center; font-size:10px; font-weight:${isDeep ? '700' : '500'}; color:${isDeep ? '#dc2626' : (isZero ? '#94a3b8' : '#0f172a')}; background:${isDeep ? '#fee2e2' : 'transparent'}; border-radius:2px;">${displayVal}</span>`;
        };
        return `<div style="display:flex; justify-content:center; gap:1px;">${renderSub(item.value)}${renderSub(item.value2)}${renderSub(item.value3)}</div>`;
      }
      const num = Number(item.value);
      const isHigh = !isNaN(num) && num > 0;
      return `<span style="display:inline-block; font-size:11px; font-weight:${isHigh ? '700' : '400'}; color:${isHigh ? '#c2410c' : '#94a3b8'};">${item.value !== undefined ? item.value : '0'}</span>`;
    }

    if (rowDef?.type === 'multiclick') {
      const renderDot = (val, color) => {
        const isActive = val == 1;
        return `<span style="display:inline-block; width:7px; height:7px; border-radius:50%; background-color:${isActive ? color : '#e2e8f0'}; margin:0 1px;"></span>`;
      };
      const activeColor = rowKey === 'Bleeding_on_probing' ? '#ef4444' : '#f59e0b';
      return `<div style="display:flex; justify-content:center; align-items:center;">${renderDot(item.value1, activeColor)}${renderDot(item.value2, activeColor)}${item.value3 !== undefined ? renderDot(item.value3, activeColor) : ''}</div>`;
    }

    // Single click
    const isActive = item.value == 1;
    if (rowKey === 'Implant') {
      return isActive ? `<span style="font-size:9px; font-weight:700; color:#0d9488; background:#ccfbf1; padding:1px 3px; border-radius:3px;">IMP</span>` : `<span style="color:#cbd5e1;">·</span>`;
    }
    if (rowKey === 'Furcation') {
      return isActive ? `<span style="font-size:10px; font-weight:700; color:#8b5cf6;">▲</span>` : `<span style="color:#cbd5e1;">·</span>`;
    }
    return isActive ? `<span style="display:inline-block; width:8px; height:8px; border-radius:50%; background-color:#3b82f6;"></span>` : `<span style="color:#cbd5e1;">·</span>`;
  };

  const renderTableRows = (jawData, positionKey, teethArr) => {
    const posObj = jawData?.[positionKey] || {};
    return Object.keys(posObj).map(rowKey => {
      const rowDef = posObj[rowKey];
      const dataArr = rowDef?.data || [];
      const cellsHtml = teethArr.map(toothNum => {
        const item = dataArr.find(d => d.tooth === toothNum);
        return `<td style="border: 1px solid #cbd5e1; padding: 2px 1px; text-align: center; height: 18px;">${renderPrintCell(rowKey, rowDef, item)}</td>`;
      }).join('');

      return `
        <tr>
          <td style="border: 1px solid #cbd5e1; padding: 2px 4px; font-weight: 600; font-size: 9.5px; background-color: #f8fafc; color: #334155; text-align: left; white-space: nowrap; width: 100px;">
            ${rowKey.replaceAll('_', ' ')}
          </td>
          ${cellsHtml}
        </tr>
      `;
    }).join('');
  };

  const renderTeethHeaderRow = (teethArr) => {
    return `
      <tr style="background-color: #e2e8f0;">
        <td style="border: 1px solid #cbd5e1; padding: 3px 4px; font-weight: 700; font-size: 9px; color: #1e293b; text-align: left;">Teeth #</td>
        ${teethArr.map(t => `<td style="border: 1px solid #cbd5e1; padding: 2px; text-align: center; font-weight: 700; font-size: 10.5px; color: #0f172a;">#${t}</td>`).join('')}
      </tr>
    `;
  };

  const html = `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <title>Periodontal Examination Chart - ${patientName}</title>
      <style>
        @page {
          size: A4 landscape;
          margin: 8mm;
        }
        * {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
        }
        body {
          background-color: #ffffff;
          color: #0f172a;
          font-size: 11px;
          line-height: 1.3;
          padding: 8px;
        }
        .header-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 8px;
          border-bottom: 2px solid #0f172a;
          padding-bottom: 6px;
        }
        .clinic-title {
          font-size: 16px;
          font-weight: 800;
          color: #0f172a;
        }
        .doc-title {
          font-size: 13px;
          font-weight: 700;
          color: #0284c7;
          text-align: right;
        }
        .patient-card {
          width: 100%;
          background-color: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          padding: 6px 10px;
          margin-bottom: 8px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .summary-bar {
          display: flex;
          gap: 12px;
          margin-bottom: 8px;
        }
        .summary-box {
          flex: 1;
          background: #ffffff;
          border: 1px solid #cbd5e1;
          border-radius: 5px;
          padding: 4px 8px;
          text-align: center;
        }
        .summary-box .val {
          font-size: 13px;
          font-weight: 700;
        }
        .summary-box .lbl {
          font-size: 9px;
          color: #64748b;
          text-transform: uppercase;
        }
        .matrix-table {
          width: 100%;
          border-collapse: collapse;
          table-layout: fixed;
          margin-bottom: 8px;
        }
        .section-header {
          background-color: #0284c7;
          color: #ffffff;
          font-size: 10px;
          font-weight: 700;
          padding: 3px 6px;
          border-radius: 3px 3px 0 0;
          display: flex;
          justify-content: space-between;
        }
        .legend-bar {
          display: flex;
          gap: 15px;
          font-size: 9.5px;
          background-color: #f1f5f9;
          padding: 4px 8px;
          border-radius: 4px;
          margin-bottom: 8px;
          align-items: center;
        }
        .sign-footer {
          margin-top: 12px;
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
        }
        @media print {
          body { padding: 0; }
          .no-print { display: none; }
        }
      </style>
    </head>
    <body>
      <!-- Header -->
      <table class="header-table">
        <tr>
          <td style="vertical-align: middle;">
            ${logoImage ? `<img src="${logoImage}" style="max-height: 42px; margin-right: 8px; vertical-align: middle;" />` : ''}
            <div style="display: inline-block; vertical-align: middle;">
              <div class="clinic-title">${clinicName}</div>
              <div style="font-size: 10px; color: #64748b;">${clinicAddress} | Tel: ${clinicPhone} | ${clinicEmail}</div>
            </div>
          </td>
          <td style="vertical-align: middle; text-align: right;">
            <div class="doc-title">PERIODONTAL EXAMINATION REPORT</div>
            <div style="font-size: 10.5px; color: #475569;">Doctor: <strong>${doctorName}</strong></div>
          </td>
        </tr>
      </table>

      <!-- Patient Information -->
      <div class="patient-card">
        <div><span style="color: #64748b;">Patient:</span> <strong>${patientName}</strong></div>
        <div><span style="color: #64748b;">UHID / Case:</span> <strong>${uhid}</strong></div>
        <div><span style="color: #64748b;">Age / Sex:</span> <strong>${ageGender}</strong></div>
        <div><span style="color: #64748b;">Date:</span> <strong>${examDate}</strong></div>
      </div>

      <!-- Clinical Periodontal Summary Scorecard -->
      <div class="summary-bar">
        <div class="summary-box">
          <div class="val" style="color: #ef4444;">${bopPercentage}% (${bopCount} Sites)</div>
          <div class="lbl">Bleeding on Probing (BOP)</div>
        </div>
        <div class="summary-box">
          <div class="val" style="color: #f59e0b;">${plaquePercentage}% (${plaqueCount} Sites)</div>
          <div class="lbl">Plaque Index (PI)</div>
        </div>
        <div class="summary-box">
          <div class="val" style="color: #dc2626;">${deepPocketsCount} Sites</div>
          <div class="lbl">Deep Pockets (≥ 4mm)</div>
        </div>
        <div class="summary-box">
          <div class="val" style="color: #0d9488;">${implantCount} Implants</div>
          <div class="lbl">Implants Present</div>
        </div>
        <div class="summary-box">
          <div class="val" style="color: #c2410c;">${mobilityCount} Teeth</div>
          <div class="lbl">Teeth with Mobility</div>
        </div>
      </div>

      <!-- Legend -->
      <div class="legend-bar">
        <strong>Legend:</strong>
        <span>🔴 Bleeding on Probing (BOP)</span>
        <span>🟡 Plaque Present (PI)</span>
        <span>▲ Furcation Involvement</span>
        <span style="background:#ccfbf1; color:#0d9488; padding:0 3px; font-weight:700; border-radius:2px;">IMP</span> Implant
        <span style="background:#fee2e2; color:#dc2626; padding:0 3px; font-weight:700; border-radius:2px;">4+</span> Pocket Depth ≥ 4mm
      </div>

      <!-- Maxillary (Upper Jaw) Matrix -->
      <div class="section-header">
        <span>MAXILLARY ARCH (UPPER JAW) - TEETH #18 TO #28</span>
        <span>PATIENT RIGHT ➔ PATIENT LEFT</span>
      </div>
      <table class="matrix-table">
        <tbody>
          ${renderTableRows(rawUpper, 'buccal', upperTeeth)}
          ${renderTeethHeaderRow(upperTeeth)}
          ${renderTableRows(rawUpper, 'lingual', upperTeeth)}
        </tbody>
      </table>

      <!-- Mandibular (Lower Jaw) Matrix -->
      <div class="section-header" style="background-color: #0369a1; margin-top: 6px;">
        <span>MANDIBULAR ARCH (LOWER JAW) - TEETH #48 TO #38</span>
        <span>PATIENT RIGHT ➔ PATIENT LEFT</span>
      </div>
      <table class="matrix-table">
        <tbody>
          ${renderTableRows(rawLower, 'lingual', lowerTeeth)}
          ${renderTeethHeaderRow(lowerTeeth)}
          ${renderTableRows(rawLower, 'buccal', lowerTeeth)}
        </tbody>
      </table>

      <!-- Footer Signature -->
      <div class="sign-footer">
        <div style="font-size: 9.5px; color: #64748b;">
          * Generated on ${moment().format('DD-MMM-YYYY HH:mm')} | Oralstop Dental Practice Management System
        </div>
        <div style="text-align: center; min-width: 180px;">
          ${signImage ? `<img src="${signImage}" style="max-height: 40px; margin-bottom: 2px;" alt="Signature" /><br/>` : ''}
          <div style="border-top: 1.5px solid #0f172a; padding-top: 3px; font-weight: 700; font-size: 11px;">${doctorName}</div>
          <div style="font-size: 9.5px; color: #64748b;">Authorized Periodontist / Dental Surgeon</div>
        </div>
      </div>

      <script>
        window.addEventListener('load', () => {
          setTimeout(() => { window.print(); }, 400);
        });
      </script>
    </body>
    </html>
  `;

  const newWindow = window.open('', '_blank');
  if (newWindow) {
    newWindow.document.write(html);
    newWindow.document.close();
  }
};

/**
 * Open high-definition, printable Petty Cash / Voucher PDF in a new tab
 */
export const openVoucherPrintWindow = (voucher, clinicData = null) => {
  if (!voucher) return;

  const isReceipt = String(voucher.receipt_type) === '1' || voucher.receipt_type === 1;
  const voucherTitle = isReceipt ? "CASH RECEIPT VOUCHER" : "PETTY CASH PAYMENT VOUCHER";
  const voucherThemeColor = isReceipt ? "#16a34a" : "#0284c7";

  const clinicName = clinicData?.clinic_name || "909 Dental OPD & Implantology Center";
  const clinicAddress = clinicData?.address || "Modern Dental Clinic & Oral Health Care Center";
  const clinicPhone = clinicData?.phone || "098220 11111";
  const clinicEmail = clinicData?.email || "billing@oralstop.com";

  const voucherNo = voucher.transection_id || `VCH-${voucher.id}`;
  const voucherDate = voucher.datetime ? moment(voucher.datetime).format('DD MMM YYYY, hh:mm A') : moment().format('DD MMM YYYY, hh:mm A');
  const totalAmount = parseFloat(voucher.total_value || 0);

  // Entries
  let entries = [];
  if (Array.isArray(voucher.voucher_transactions) && voucher.voucher_transactions.length > 0) {
    entries = voucher.voucher_transactions;
  } else if (Array.isArray(voucher.voucherEntries) && voucher.voucherEntries.length > 0) {
    entries = voucher.voucherEntries;
  } else {
    entries = [{
      description: voucher.transection_description ? voucher.transection_description.replace(/_/g, ' ') : (isReceipt ? "Cash Received" : "Cash Expense"),
      entity: isReceipt ? "Payer" : "Payee",
      amount: totalAmount
    }];
  }

  const rowsHtml = entries.map((entry, idx) => {
    const category = typeof entry.type === 'object' ? (entry.type.label || entry.type.value) : (entry.description || entry.type || "General");
    const formattedCat = String(category).replace(/_/g, ' ');
    const payee = entry.entity || entry.remark || "—";
    const amt = parseFloat(entry.amount || 0);

    return `
      <tr>
        <td style="text-align: center; color: #64748b;">${idx + 1}</td>
        <td><strong style="color: #0f172a;">${formattedCat}</strong></td>
        <td style="color: #334155;">${payee}</td>
        <td style="text-align: right; font-weight: 700; color: #0f172a;">₹${amt.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
      </tr>
    `;
  }).join('');

  const html = `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <title>${voucherTitle} - ${voucherNo}</title>
      <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body {
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
          background: #f1f5f9;
          color: #0f172a;
          padding: 24px;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .action-bar {
          max-width: 780px;
          margin: 0 auto 16px auto;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .btn {
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          border: 1px solid transparent;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .btn-primary { background: ${voucherThemeColor}; color: #fff; }
        .btn-primary:hover { opacity: 0.9; }
        .btn-secondary { background: #fff; border-color: #cbd5e1; color: #334155; }
        .btn-secondary:hover { background: #f8fafc; }

        .voucher-card {
          max-width: 780px;
          margin: 0 auto;
          background: #ffffff;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 4px 12px rgba(0,0,0,0.06);
          padding: 32px 36px;
        }

        .header-box {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          border-bottom: 2px solid ${voucherThemeColor};
          padding-bottom: 18px;
          margin-bottom: 20px;
        }
        .clinic-title {
          font-size: 20px;
          font-weight: 800;
          color: #0f172a;
          margin-bottom: 4px;
        }
        .clinic-sub {
          font-size: 11.5px;
          color: #64748b;
          line-height: 1.45;
          max-width: 420px;
        }
        .voucher-badge {
          text-align: right;
        }
        .badge-type {
          display: inline-block;
          background: ${voucherThemeColor};
          color: #ffffff;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 0.5px;
          padding: 4px 10px;
          border-radius: 4px;
          margin-bottom: 6px;
        }
        .voucher-no {
          font-size: 14px;
          font-weight: 800;
          color: #0f172a;
          font-family: monospace;
        }
        .voucher-date {
          font-size: 11px;
          color: #64748b;
          margin-top: 2px;
        }

        .meta-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 12px 16px;
          margin-bottom: 20px;
          font-size: 12px;
        }

        table.items-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 20px;
        }
        table.items-table th {
          background: #f8fafc;
          border-bottom: 2px solid #cbd5e1;
          border-top: 1px solid #e2e8f0;
          padding: 10px 12px;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          color: #475569;
          text-align: left;
        }
        table.items-table td {
          border-bottom: 1px solid #e2e8f0;
          padding: 11px 12px;
          font-size: 12px;
        }

        .total-box {
          display: flex;
          justify-content: flex-end;
          margin-bottom: 24px;
        }
        .total-card {
          width: 280px;
          background: #f8fafc;
          border: 1.5px solid #cbd5e1;
          border-radius: 8px;
          padding: 12px 16px;
        }
        .total-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 14px;
          font-weight: 800;
          color: #0f172a;
        }
        .total-amt {
          font-size: 18px;
          color: ${voucherThemeColor};
        }

        .remark-box {
          background: #f8fafc;
          border-left: 3px solid ${voucherThemeColor};
          padding: 10px 14px;
          border-radius: 4px;
          font-size: 11.5px;
          color: #475569;
          margin-bottom: 24px;
        }

        .sign-grid {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 20px;
          margin-top: 36px;
          padding-top: 12px;
          border-top: 1px dashed #cbd5e1;
        }
        .sign-col {
          text-align: center;
        }
        .sign-space {
          height: 45px;
        }
        .sign-line {
          border-top: 1.5px solid #64748b;
          padding-top: 5px;
          font-size: 11px;
          font-weight: 700;
          color: #334155;
        }
        .sign-sub {
          font-size: 9.5px;
          color: #94a3b8;
        }

        @media print {
          body { background: #ffffff; padding: 0; }
          .action-bar { display: none; }
          .voucher-card {
            border: none;
            box-shadow: none;
            padding: 15px;
            max-width: 100%;
          }
        }
      </style>
    </head>
    <body>
      <div class="action-bar">
        <div>
          <button class="btn btn-secondary" onclick="window.close()">✕ Close</button>
        </div>
        <div>
          <button class="btn btn-primary" onclick="window.print()">🖨 Print Voucher</button>
        </div>
      </div>

      <div class="voucher-card">
        <div class="header-box">
          <div>
            <div class="clinic-title">${clinicName}</div>
            <div class="clinic-sub">
              ${clinicAddress}<br/>
              Phone: ${clinicPhone} • Email: ${clinicEmail}
            </div>
          </div>
          <div class="voucher-badge">
            <div class="badge-type">${voucherTitle}</div>
            <div class="voucher-no">#${voucherNo}</div>
            <div class="voucher-date">${voucherDate}</div>
          </div>
        </div>

        <div class="meta-grid">
          <div>
            <span style="color: #64748b; font-size: 10.5px; text-transform: uppercase; font-weight: 700;">Voucher Type</span>
            <div style="font-weight: 700; color: #0f172a; margin-top: 2px;">
              ${isReceipt ? "Cash Receipt (Inflow)" : "Cash Expense / Payment (Outflow)"}
            </div>
          </div>
          <div style="text-align: right;">
            <span style="color: #64748b; font-size: 10.5px; text-transform: uppercase; font-weight: 700;">Total Entries</span>
            <div style="font-weight: 700; color: #0f172a; margin-top: 2px;">
              ${entries.length} Item(s)
            </div>
          </div>
        </div>

        <table class="items-table">
          <thead>
            <tr>
              <th style="width: 40px; text-align: center;">#</th>
              <th>Category / Account Head</th>
              <th>${isReceipt ? "Paid By (Payer)" : "Paid To (Payee)"}</th>
              <th style="text-align: right; width: 140px;">Amount (₹)</th>
            </tr>
          </thead>
          <tbody>
            ${rowsHtml}
          </tbody>
        </table>

        <div class="total-box">
          <div class="total-card">
            <div class="total-row">
              <span>Total Amount:</span>
              <span class="total-amt">₹${totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
          </div>
        </div>

        ${voucher.remark ? `
          <div class="remark-box">
            <strong>Notes / Narration:</strong> ${voucher.remark}
          </div>
        ` : ''}

        <div class="sign-grid">
          <div class="sign-col">
            <div class="sign-space"></div>
            <div class="sign-line">Prepared By</div>
            <div class="sign-sub">Accountant / Cashier</div>
          </div>
          <div class="sign-col">
            <div class="sign-space"></div>
            <div class="sign-line">${isReceipt ? "Received By" : "Receiver's Signature"}</div>
            <div class="sign-sub">${isReceipt ? "Clinic Cash Desk" : "Vendor / Staff Payee"}</div>
          </div>
          <div class="sign-col">
            <div class="sign-space"></div>
            <div class="sign-line">Authorized Signatory</div>
            <div class="sign-sub">Doctor / Clinic Head</div>
          </div>
        </div>
      </div>

      <script>
        window.addEventListener('load', () => {
          setTimeout(() => { window.print(); }, 400);
        });
      </script>
    </body>
    </html>
  `;

  const newWindow = window.open('', '_blank');
  if (newWindow) {
    newWindow.document.write(html);
    newWindow.document.close();
  }
};


